/**
 * Static content for the Social Screening Brief wizard.
 *
 * Mirrors the design handover's `data.js`: the restriction taxonomy is honest to the client's
 * enrichment CSV, and every string here is the copy signed off in the handover.
 */

/** Icon keys resolved by an inline `@switch` in the red-flags template (the app has no icon lib). */
export type RestrictionCategoryIcon = 'wine' | 'image' | 'shirt' | 'tag';

export interface RestrictionCategory {
  id: string;
  label: string;
  icon: RestrictionCategoryIcon;
  /** The clarifying question shown at the top of the category panel. */
  question: string;
  /** Sub-line under the question, framing how the AI should treat this category. */
  prompt: string;
  /** The pickable rules. Selecting one adds it to the content no-go list. */
  enrichments: string[];
}

/** Category id used for free-text rules the user writes themselves. */
export const CUSTOM_RESTRICTION_CATEGORY_ID = 'Custom';

export const RESTRICTION_CATEGORIES: RestrictionCategory[] = [
  {
    id: 'Alcohol',
    label: 'Alcohol',
    icon: 'wine',
    question: 'What are your rules on alcohol?',
    prompt: "How should the AI treat alcohol appearing in a creator's content?",
    enrichments: [
      'No alcohol visible anywhere',
      'No alcohol consumption shown',
      'No branded alcohol products',
      'No excessive drinking or intoxication shown',
      'No glamourising or promoting alcohol',
      'No alcohol references at all',
      'Alcohol in background only okay',
      'Alcohol visible but not consumed okay',
      'Social/moderate drinking okay',
      'No restrictions on alcohol',
    ],
  },
  {
    id: 'Backgrounds_Setting',
    label: 'Backgrounds & Setting',
    icon: 'image',
    question: 'Any rules about backgrounds or the setting?',
    prompt: "What should — and shouldn't — appear in the environment behind the creator?",
    enrichments: [
      'No cluttered or busy backgrounds',
      'No messy or untidy spaces',
      'No branded items in background',
      'No personal items visible',
      'No identifiable locations',
      'Plain/neutral backgrounds only',
      'Home environment okay if tidy',
      'Natural/authentic settings okay',
      'Public locations okay',
      'No restrictions on backgrounds',
    ],
  },
  {
    id: 'Clothing_Dress_Code',
    label: 'Clothing & Dress Code',
    icon: 'shirt',
    question: 'How should creators be dressed?',
    prompt: 'Set expectations for what creators wear on camera.',
    enrichments: [
      'No revealing clothing',
      'No swimwear or underwear',
      'No offensive slogans or imagery on clothing',
      'No competitor branded clothing',
      'Formal attire only',
      'Smart casual only',
      'Branded clothing okay if approved',
      'Activewear/sportswear okay',
      'Casual clothing okay',
      'No restrictions on clothing',
    ],
  },
  {
    id: 'Competitor_Logos_Branding_Visible',
    label: 'Competitor Logos & Branding',
    icon: 'tag',
    question: 'What about competitor logos or branding on screen?',
    prompt: 'Decide how visible rival brands are allowed to be.',
    enrichments: [
      'No competitor branding visible at all',
      'No competitor logos in shot',
      'No competitor packaging visible',
      'No competitor products in background',
      'Must remove or cover if prominent',
      'Blurred/covered competitor branding okay',
      'Incidental background branding okay',
      'Unrelated brand logos okay',
      'No restrictions on visible branding',
    ],
  },
];

/** One-click example rules under the custom red-flag input — the AI can spot anything. */
export const CUSTOM_RESTRICTION_EXAMPLES: string[] = [
  'No filming while driving a car',
  'No vaping or e-cigarettes on camera',
  'No visible tattoos with text or slogans',
  'No political slogans or campaign material',
  'No gambling or betting references',
  'No fast-food or takeaway packaging in shot',
  'No filming in a moving vehicle',
  'No pets on camera during the product demo',
];

/** "How to brief the AI" pointers shown beside the requirements textarea. */
export const SCREEN_LOOKFOR_POINTERS: string[] = [
  'These are the positives — the qualities and content you WANT to see, not what to avoid.',
  'Be as specific or as broad as you like — the AI reads every frame, caption and comment.',
  'Describe the vibe you need creators to embody, and the topics or moments that signal a good fit.',
  'You can reference tone, language, on-screen text, and who else appears.',
];

/** What the standard screening report contains. */
export const STANDARD_REPORT_INCLUDES: string[] = [
  'A pass, flag or fail verdict per creator',
  'Every requirement matched, with the post that proves it',
  'Every red flag triggered, with a link to the evidence',
  'Sentiment read across captions and comments',
  'Follower and engagement figures per platform',
  'Full cohort exportable as CSV',
];

/** Starter lines appended to the custom-analysis textarea. */
export const REPORT_PROMPT_EXAMPLES: string[] = [
  'Rank creators by how often they mention sustainability',
  'Group results by platform and flag anyone under 3% engagement',
  'One paragraph per creator written for a client-facing deck',
  'Only surface creators who pass every requirement',
];

/** The three stages shown while the pre-flight check runs. */
export const PREFLIGHT_STAGES: string[] = [
  'Validating your requirements',
  'Reviewing your red-flag rules',
  'Confirming the brief has enough depth',
];

/** The stages shown while the brief is being submitted. */
export const SUBMIT_STAGES: string[] = [
  'Creating your screening brief',
  'Attaching the creators to screen',
  'Handing the brief to the screening engine',
];
